import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Flame, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getHotComparison, HOT_COMPARISONS } from "@/lib/hot-compare";
import { getHotCompareContent } from "@/lib/hot-compare.i18n";
import { BOARDS_OG_IMAGE, BOARDS_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useLocale, useT } from "@/i18n";
import { detectLocale } from "@/i18n/runtime";

export const Route = createFileRoute("/compare/hot/$competitor")({
  loader: ({ params }) => {
    const cmp = getHotComparison(params.competitor);
    if (!cmp) throw notFound();
    return { slug: cmp.slug };
  },
  head: ({ params }) => {
    const cmp = getHotComparison(params.competitor);
    if (!cmp) {
      return buildPageMetadata({
        path: `/compare/hot/${params.competitor}`,
        title: "Comparison not found | BeHot.Now",
        description: "This comparison is not available on BeHot.Now.",
        noindex: true,
      });
    }
    const c = getHotCompareContent(detectLocale(), cmp);
    return buildPageMetadata({
      path: `/compare/hot/${cmp.slug}`,
      title: c.title,
      description: c.description,
      ogTitle: c.h1,
      ogDescription: c.description,
      image: BOARDS_OG_IMAGE,
      imageAlt: BOARDS_OG_IMAGE_ALT,
      keywords: cmp.keywords,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "HOT", path: "/hot" },
        { name: `vs ${cmp.brand}`, path: `/compare/hot/${cmp.slug}` },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: c.h1,
        url: `${SITE_URL}/compare/hot/${cmp.slug}`,
        description: c.description,
        isPartOf: { "@type": "WebSite", url: SITE_URL, name: "BeHot.Now" },
      },
    });
  },
  component: ComparePage,
});

function ComparePage() {
  const { competitor } = Route.useParams();
  const locale = useLocale();
  const { t } = useT();
  const base = getHotComparison(competitor);
  const c = base ? getHotCompareContent(locale, base) : null;

  if (!base || !c) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">{t("hotc.compareNotFound")}</h1>
        <Button asChild variant="hot" className="mt-4">
          <Link to="/hot">{t("hotc.goToBoards")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          {t("hotc.home")}
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/hot" className="hover:text-foreground">
          {t("hotc.hot")}
        </Link>
      </nav>

      <header className="mt-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
          <Scale className="size-3.5" /> {t("hotc.comparison")}
        </span>
        <h1 className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl">{c.h1}</h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">{c.intro}</p>
      </header>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild variant="hot">
          <Link to="/hot">
            {t("hotc.hub.liveBoards")} <ArrowRight className="ml-1 size-4" />
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/get-hot" search={{ category: undefined, listing: undefined }}>
            {t("hotc.getOnBoard")}
          </Link>
        </Button>
      </div>

      <div className="mt-8 space-y-4">
        {c.summary.map((p, i) => (
          <p key={i} className="leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}
      </div>

      <section className="mt-10">
        <h2 className="font-display text-xl font-bold sm:text-2xl">
          BeHot.Now vs {base.brand}
        </h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t("hotc.dimension")}
                </th>
                <th scope="col" className="py-2 pr-4 font-semibold text-primary">
                  <span className="inline-flex items-center gap-1">
                    <Flame className="size-4" /> BeHot.Now
                  </span>
                </th>
                <th scope="col" className="py-2 font-semibold">
                  {base.brand}
                </th>
              </tr>
            </thead>
            <tbody>
              {c.rows.map((row, i) => (
                <tr key={i} className="border-b border-border/60 align-top">
                  <th scope="row" className="py-3 pr-4 text-left font-medium">
                    {row.dimension}
                  </th>
                  <td className="py-3 pr-4 text-muted-foreground">{row.behot}</td>
                  <td className="py-3 text-muted-foreground">{row.them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {base.source && (
          <p className="mt-3 text-xs text-muted-foreground">
            {t("hotc.sourcePrefix", { brand: base.brand })}{" "}
            <a
              href={base.source}
              rel="nofollow noopener noreferrer"
              target="_blank"
              className="underline"
            >
              {t("hotc.sourceLink")}
            </a>
            {base.reviewed ? ` ${t("hotc.sourceChecked", { date: base.reviewed })}` : "."}{" "}
            {t("hotc.sourceNote")}
          </p>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-display text-xl font-bold sm:text-2xl">{t("hotc.questions")}</h2>
        <dl className="mt-4 space-y-4">
          {c.faq.map(([q, a], i) => (
            <div key={i} className="card-surface p-4">
              <dt className="font-semibold">{q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {t("hotc.moreComparisons")}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {HOT_COMPARISONS.filter((x) => x.slug !== base.slug).map((x) => (
            <Link
              key={x.slug}
              to="/compare/hot/$competitor"
              params={{ competitor: x.slug }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              {t("hotc.vs", { brand: x.brand })}
            </Link>
          ))}
          <Link
            to="/hot/$topic"
            params={{ topic: "howitworks" }}
            className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t("hotc.hiwBoards")}
          </Link>
        </div>
      </section>
    </div>
  );
}
